/**
 * UnifiedResolutionDialog Component
 * پنل یکپارچه مدیریت اختلاف - Minimal RTL Design
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, CircularProgress, Slider } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import GavelIcon from '@mui/icons-material/Gavel';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonIcon from '@mui/icons-material/Person';
import InventoryIcon from '@mui/icons-material/Inventory';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import LoopIcon from '@mui/icons-material/Loop';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import BlockIcon from '@mui/icons-material/Block';
import CancelIcon from '@mui/icons-material/Cancel';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import ChatIcon from '@mui/icons-material/Chat';

import './UnifiedResolutionDialog.css';

// ========== Types ==========

export type DisputeType = 'order' | 'order_item' | 'inspection';
export type DisputeStatus = 'pending_admin_approval' | 'approved' | 'investigating' | 'resolved' | 'closed' | 'rejected';
export type ActionType = 'approve' | 'reject' | 'refund' | 'resolve_buyer' | 'resolve_seller' | 'resolve_mutual' | 'change_status' | 'cancel_order';
export type ItemStatus = 'pending' | 'processing' | 'delivered' | 'disputed' | 'cancelled' | 'refunded';

export interface DisputeParty {
  id: string;
  name: string;
  email?: string;
  role: 'buyer' | 'seller' | 'party_a' | 'party_b';
}

export interface FinancialInfo {
  amount: number;
  shippingCost?: number;
  refundableAmount: number;
}

export interface DisputeData {
  id: string;
  disputeNumber: string;
  type: DisputeType;
  status: DisputeStatus;
  reason: string;
  reasonLabel: string;
  description: string;
  parties: {
    initiator: DisputeParty;
    respondent?: DisputeParty;
  };
  financial?: FinancialInfo;
  references: {
    orderId?: string;
    orderNumber?: string;
    orderItemId?: string;
    productTitle?: string;
    storeName?: string;
  };
  currentItemStatus?: string;
  chatId?: string;
}

export interface ActionPayload {
  actionType: ActionType;
  reason?: string;
  resolution?: string;
  resolutionType?: string;
  refundAmount?: number;
  applyPenalty?: boolean;
  penaltyPoints?: number;
  includeShipping?: boolean;
  newStatus?: string;
  tags?: string[];
}

interface Props {
  open: boolean;
  onClose: () => void;
  dispute: DisputeData | null;
  onAction: (action: ActionType, payload: ActionPayload) => Promise<void>;
  loading?: boolean;
  error?: string | null;
}

// ========== Constants ==========

const ITEM_STATUSES = [
  { value: 'pending' as ItemStatus, label: 'در انتظار', color: '#ff9800', bg: '#fff3e0', icon: <HourglassEmptyIcon />, desc: 'سفارش هنوز پردازش نشده', needsRefund: false },
  { value: 'processing' as ItemStatus, label: 'در حال پردازش', color: '#2196f3', bg: '#e3f2fd', icon: <LoopIcon />, desc: 'فروشنده در حال آماده‌سازی', needsRefund: false },
  { value: 'delivered' as ItemStatus, label: 'تحویل داده شده', color: '#4caf50', bg: '#e8f5e9', icon: <LocalShippingIcon />, desc: 'محصول تحویل داده شده', needsRefund: false },
  { value: 'disputed' as ItemStatus, label: 'اختلاف دارد', color: '#e91e63', bg: '#fce4ec', icon: <BlockIcon />, desc: 'در حال بررسی اختلاف', needsRefund: false },
  { value: 'cancelled' as ItemStatus, label: 'لغو شده', color: '#f44336', bg: '#ffebee', icon: <CancelIcon />, desc: 'سفارش لغو می‌شود', needsRefund: true },
  { value: 'refunded' as ItemStatus, label: 'بازپرداخت شده', color: '#9c27b0', bg: '#f3e5f5', icon: <MonetizationOnIcon />, desc: 'مبلغ برگشت داده می‌شود', needsRefund: true }
];

const formatPrice = (p: number) => new Intl.NumberFormat('fa-IR').format(p);
const getTypeLabel = (t: DisputeType) => ({ order: 'سفارش', order_item: 'محصول', inspection: 'بازرسی' }[t]);

// ========== Component ==========

const UnifiedResolutionDialog: React.FC<Props> = ({ open, onClose, dispute, onAction, loading = false, error = null }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState<'select' | 'refund'>('select');
  const [selectedStatus, setSelectedStatus] = useState<ItemStatus | null>(null);
  const [reason, setReason] = useState('');
  const [refundAmount, setRefundAmount] = useState(0);
  const [applyPenalty, setApplyPenalty] = useState(true);
  const [penaltyPoints, setPenaltyPoints] = useState(10);
  const [includeShipping, setIncludeShipping] = useState(true);

  useEffect(() => {
    if (open && dispute) {
      setStep('select');
      setSelectedStatus(null);
      setReason('');
      setRefundAmount(dispute.financial?.refundableAmount || 0);
      setApplyPenalty(true);
      setPenaltyPoints(10);
      setIncludeShipping(true);
    }
  }, [open, dispute]);

  if (!dispute) return null;

  const currentStatus = dispute.currentItemStatus || 'pending';
  const currentConfig = ITEM_STATUSES.find(s => s.value === currentStatus);
  const selectedConfig = selectedStatus ? ITEM_STATUSES.find(s => s.value === selectedStatus) : null;
  const maxAmount = dispute.financial?.refundableAmount || 0;
  const productPrice = dispute.financial?.amount || 0;
  const shippingCost = dispute.financial?.shippingCost || 0;
  const percentage = maxAmount > 0 ? Math.round((refundAmount / maxAmount) * 100) : 0;

  const handleStatusSelect = async (status: ItemStatus) => {
    setSelectedStatus(status);
    const config = ITEM_STATUSES.find(s => s.value === status);
    if (config?.needsRefund) {
      setStep('refund');
    } else {
      await executeAction(status, '');
    }
  };

  const executeAction = async (status: ItemStatus, actionReason: string) => {
    if (!dispute) return;

    let actionType: ActionType;
    let payload: ActionPayload;

    if (status === 'refunded') {
      actionType = 'refund';
      payload = { actionType: 'refund', newStatus: status, reason: actionReason || 'بازپرداخت بابت حل اختلاف', refundAmount, applyPenalty, penaltyPoints: applyPenalty ? penaltyPoints : 0, includeShipping };
    } else if (status === 'cancelled') {
      actionType = 'cancel_order';
      payload = { actionType: 'cancel_order', newStatus: status, reason: actionReason || 'لغو توسط ادمین' };
    } else {
      actionType = 'change_status';
      payload = { actionType: 'change_status', newStatus: status, reason: actionReason };
    }

    await onAction(actionType, payload);
  };

  const handleSubmit = async () => {
    if (!selectedStatus) return;
    await executeAction(selectedStatus, reason);
  };

  const handleClose = () => { if (!loading) onClose(); };
  const handleBack = () => { setStep('select'); setSelectedStatus(null); };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth className="resolution-dialog">
      {/* Header */}
      <div className="resolution-header">
        <div className="resolution-header-info">
          <div className="resolution-header-icon"><GavelIcon /></div>
          <div className="resolution-header-text">
            <h2>{step === 'select' ? 'تغییر وضعیت آیتم' : 'تکمیل بازپرداخت'}</h2>
            <span>{getTypeLabel(dispute.type)} - #{dispute.disputeNumber}</span>
          </div>
        </div>
        <button className="resolution-close" onClick={handleClose} disabled={loading}><CloseIcon fontSize="small" /></button>
      </div>

      {/* Content */}
      <div className="resolution-content">
        {/* Error */}
        {error && (
          <div className="resolution-alert warning">
            <WarningIcon />
            <div className="resolution-alert-text">{error}</div>
          </div>
        )}

        {/* Summary */}
        <div className="resolution-summary">
          <span className="resolution-summary-badge primary">#{dispute.disputeNumber}</span>
          {dispute.references.productTitle && (
            <div className="resolution-summary-item">
              <InventoryIcon />
              <span style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{dispute.references.productTitle}</span>
            </div>
          )}
          <div className="resolution-summary-item">
            <PersonIcon />
            <span>{dispute.parties.initiator.name}</span>
          </div>
          {dispute.financial && (
            <span className="resolution-summary-badge success">{formatPrice(dispute.financial.refundableAmount)} تومان</span>
          )}
        </div>

        {/* Chat Link Button */}
        {dispute.chatId && (
          <button className="chat-link-btn" onClick={() => { onClose(); navigate(`/arbitration/${dispute.chatId}`); }}>
            <ChatIcon />
            رفتن به صفحه داوری و چت
          </button>
        )}

        {/* Step 1: Select Status */}
        {step === 'select' && (
          <>
            {/* Current Status */}
            <div className="current-status">
              <div className="current-status-label">وضعیت فعلی:</div>
              <span className="current-status-badge" style={{ background: currentConfig?.color }}>
                {currentConfig?.icon}
                {currentConfig?.label || currentStatus}
              </span>
            </div>

            <div className="resolution-divider" />

            <div className="section-title">وضعیت جدید را انتخاب کنید:</div>

            <div className="status-cards">
              {ITEM_STATUSES.filter(s => s.value !== currentStatus).map(status => (
                <div
                  key={status.value}
                  className={`status-card ${selectedStatus === status.value ? 'selected' : ''} ${loading ? 'disabled' : ''}`}
                  style={{ '--card-color': status.color, '--card-bg': status.bg } as React.CSSProperties}
                  onClick={() => !loading && handleStatusSelect(status.value)}
                >
                  <div className="status-card-icon" style={{ background: status.bg, color: status.color }}>
                    {status.icon}
                  </div>
                  <div className="status-card-info">
                    <div className="status-card-title">{status.label}</div>
                    <div className="status-card-desc">{status.desc}</div>
                  </div>
                  {status.needsRefund && <span className="status-card-badge">نیاز به فرم</span>}
                  {loading && selectedStatus === status.value && <CircularProgress size={20} className="status-card-loading" />}
                </div>
              ))}
            </div>
          </>
        )}

        {/* Step 2: Refund Form */}
        {step === 'refund' && selectedConfig && (
          <>
            {/* Alert */}
            <div className={`resolution-alert ${selectedStatus === 'refunded' ? 'info' : 'warning'}`}>
              {selectedStatus === 'refunded' ? <InfoIcon /> : <WarningIcon />}
              <div className="resolution-alert-text">
                {selectedStatus === 'refunded'
                  ? 'مبلغ بازپرداخت به کیف پول کاربر واریز می‌شود.'
                  : 'آیتم سفارش لغو می‌شود. می‌توانید مبلغ را بازپرداخت کنید.'}
              </div>
            </div>

            {/* Financial Summary */}
            <div className="financial-summary">
              <div className="financial-summary-title">💰 خلاصه مالی</div>
              <div className="financial-row">
                <span className="financial-label">قیمت محصول:</span>
                <span className="financial-value">{formatPrice(productPrice)} تومان</span>
              </div>
              {shippingCost > 0 && (
                <div className="financial-row">
                  <span className="financial-label">هزینه ارسال:</span>
                  <span className="financial-value">{formatPrice(shippingCost)} تومان</span>
                </div>
              )}
              <div className="financial-row total">
                <span className="financial-label">حداکثر قابل بازپرداخت:</span>
                <span className="financial-value">{formatPrice(maxAmount)} تومان</span>
              </div>
            </div>

            {/* Refund Amount */}
            <div className="refund-amount-section">
              <div className="refund-amount-header">
                <span className="section-title">مبلغ بازپرداخت</span>
                <span className={`refund-percentage ${percentage === 100 ? 'full' : ''}`}>{percentage}%</span>
              </div>
              <div className="refund-slider">
                <Slider
                  value={refundAmount}
                  onChange={(_, v) => setRefundAmount(v as number)}
                  max={maxAmount}
                  step={1000}
                  valueLabelDisplay="auto"
                  valueLabelFormat={v => `${formatPrice(v)} ت`}
                />
              </div>
              <input
                type="number"
                className="refund-input"
                value={refundAmount}
                onChange={e => setRefundAmount(Math.min(Number(e.target.value), maxAmount))}
                placeholder="مبلغ به تومان"
              />
            </div>

            {/* Reason */}
            <div className="form-field">
              <label>دلیل (اختیاری)</label>
              <textarea
                className="form-textarea"
                value={reason}
                onChange={e => setReason(e.target.value)}
                placeholder="مثال: عدم تطابق محصول با توضیحات"
              />
              <div className="hint">این متن به کاربر نمایش داده می‌شود</div>
            </div>

            {/* Include Shipping */}
            {shippingCost > 0 && (
              <label className="checkbox-item">
                <input type="checkbox" checked={includeShipping} onChange={e => setIncludeShipping(e.target.checked)} />
                <span>شامل هزینه ارسال ({formatPrice(shippingCost)} تومان)</span>
              </label>
            )}

            {/* Penalty Section */}
            <div className="penalty-section">
              <label className="penalty-header">
                <input type="checkbox" checked={applyPenalty} onChange={e => setApplyPenalty(e.target.checked)} />
                <span>اعمال جریمه به فروشنده</span>
              </label>
              {applyPenalty && (
                <div className="penalty-slider-section">
                  <div className="penalty-slider-label">امتیاز منفی:</div>
                  <Slider
                    value={penaltyPoints}
                    onChange={(_, v) => setPenaltyPoints(v as number)}
                    min={1}
                    max={50}
                    step={1}
                    valueLabelDisplay="on"
                  />
                  <div className="penalty-marks">
                    <span>۱</span>
                    <span>۲۵</span>
                    <span>۵۰</span>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

      </div>

      {/* Actions */}
      <div className={`resolution-actions ${step === 'refund' ? 'has-back' : ''}`}>
        {step === 'refund' && (
          <button className="action-btn back" onClick={handleBack} disabled={loading}>
            <ArrowBackIcon fontSize="small" /> بازگشت
          </button>
        )}
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="action-btn cancel" onClick={handleClose} disabled={loading}>انصراف</button>
          {step === 'refund' && (
            <button
              className={`action-btn ${selectedStatus === 'refunded' ? 'info' : 'warning'}`}
              onClick={handleSubmit}
              disabled={loading || refundAmount <= 0}
            >
              {loading ? <CircularProgress size={18} color="inherit" /> : selectedConfig?.icon}
              {loading ? 'در حال انجام...' : selectedStatus === 'refunded' ? `بازپرداخت ${formatPrice(refundAmount)} تومان` : `لغو و بازپرداخت ${formatPrice(refundAmount)} تومان`}
            </button>
          )}
        </div>
      </div>
    </Dialog>
  );
};

export default UnifiedResolutionDialog;
